import { useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Camera, FileText, Upload, CheckCircle, Loader2, Image, Bike } from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function QuickRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=create, 2=carte grise, 3=photos, 4=done
  const [scooterData, setScooterData] = useState(null);
  const [aiData, setAiData] = useState(null);
  const [form, setForm] = useState({ brand: "", model: "", year: "", engine_cc: "", color: "", mileage: "", vin: "" });
  const [country, setCountry] = useState("fr");
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Step 1: Create scooter with dossier number
  const handleCreate = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/scooters/quick-register`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScooterData(res.data);
      toast.success(`Dossier nr. ${res.data.dossier_number} creat!`);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare");
    }
    setLoading(false);
  };

  // Step 2: Upload and analyze carte grise
  const handleCarteGrise = async (file) => {
    if (!file || !scooterData) return;
    setAnalyzing(true);
    
    // Upload carte grise photo (private)
    const token = localStorage.getItem("token");
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/scooters/${scooterData.scooter_id}/carte-grise-photo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
    } catch (e) { console.warn("Upload error:", e); }

    // AI analyze
    toast.loading("AI analizeaza carte grise...");
    const fd2 = new FormData();
    fd2.append("file", file);
    fd2.append("country", country);
    try {
      const res = await axios.post(`${API}/ai/analyze-carte-grise?country=${country}`, fd2, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.dismiss();
      if (res.data.success && res.data.data) {
        const d = res.data.data;
        setAiData(d);
        setForm({
          brand: d.brand || "",
          model: d.model || "",
          year: d.year?.toString() || "",
          engine_cc: d.engine_cc?.toString() || "",
          color: d.color || "",
          mileage: "",
          vin: d.vin || "",
        });
        toast.success("Date extrase cu succes de AI!");
      } else {
        toast.error("AI nu a putut extrage datele. Completati manual.");
      }
    } catch (err) {
      toast.dismiss();
      toast.error("Eroare AI. Completati manual.");
    }
    setAnalyzing(false);
  };

  // Save scooter data
  const handleSaveData = async () => {
    if (!scooterData) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const update = {
        brand: form.brand,
        model: form.model,
        year: parseInt(form.year) || 0,
        engine_cc: parseInt(form.engine_cc) || 0,
        color: form.color,
        mileage: parseInt(form.mileage) || 0,
        vin: form.vin,
      };
      if (aiData) {
        update.technical_file = {
          engine_type: aiData.engine_type || "",
          transmission: aiData.transmission || "CVT",
          fuel_type: aiData.fuel_type || "Benzina",
          max_speed: aiData.max_speed || "",
          consumption: aiData.consumption || "",
          injection_type: aiData.injection_type || "",
          power_kw: aiData.power_kw || "",
          power_hp: aiData.power_hp || "",
          tip_acte: country === "fr" ? "carte_strainatate" : country === "nl" ? "carte_strainatate" : "carte_rar",
          tara_documente: country === "fr" ? "Franta" : country === "nl" ? "Olanda" : "Romania",
        };
      }
      await axios.put(`${API}/scooters/${scooterData.scooter_id}`, update, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
      });
      toast.success("Date salvate!");
      setStep(3);
    } catch (err) {
      toast.error("Eroare salvare");
    }
    setLoading(false);
  };

  // Upload scooter photos
  const handlePhotos = async (files) => {
    if (!scooterData) return;
    setLoading(true);
    const token = localStorage.getItem("token");
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("photo_type", "general");
        await axios.post(`${API}/scooters/${scooterData.scooter_id}/photos`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
        });
      } catch (e) { toast.error(`Eroare: ${file.name}`); }
    }
    toast.success(`${files.length} fotografii adaugate!`);
    setLoading(false);
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto space-y-4" data-testid="quick-register-page">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Plus className="w-7 h-7 text-primary" /> Inregistrare rapida scuter
        </h1>

        {/* Progress steps */}
        <div className="flex items-center gap-2 text-xs">
          {[
            { n: 1, label: "Creare dossier" },
            { n: 2, label: "Carte grise + AI" },
            { n: 3, label: "Fotografii" },
            { n: 4, label: "Finalizat" },
          ].map(s => (
            <div key={s.n} className={`flex items-center gap-1 px-3 py-1 rounded-full ${step >= s.n ? "bg-primary text-black font-bold" : "bg-secondary text-muted-foreground"}`}>
              {step > s.n ? <CheckCircle className="w-3 h-3" /> : s.n}. {s.label}
            </div>
          ))}
        </div>

        {/* Step 1: Create */}
        {step === 1 && (
          <Card>
            <CardContent className="p-6 text-center space-y-4">
              <Bike className="w-16 h-16 text-primary mx-auto" />
              <h2 className="text-xl font-bold">Creati un nou dossier scuter</h2>
              <p className="text-sm text-muted-foreground">Se va genera automat un numar de dossier cu 5 cifre</p>
              <Button onClick={handleCreate} size="lg" disabled={loading} className="w-full">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Creaza dossier
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Carte grise */}
        {step === 2 && scooterData && (
          <div className="space-y-4">
            <Card className="border-primary/30">
              <CardContent className="p-4">
                <Badge className="bg-primary text-black mb-2">Dossier nr. {scooterData.dossier_number}</Badge>
                <p className="font-mono text-sm">{scooterData.scooter_id}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-bold flex items-center gap-2"><FileText className="w-4 h-4 text-primary" /> Carte grise</h3>
                
                <div className="space-y-2">
                  <Label>Tara document</Label>
                  <Select value={country} onValueChange={setCountry}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Franta</SelectItem>
                      <SelectItem value="nl">Olanda</SelectItem>
                      <SelectItem value="ro">Romania</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <label className="block cursor-pointer">
                  <input type="file" accept="image/*" capture="camera" className="hidden" onChange={e => e.target.files[0] && handleCarteGrise(e.target.files[0])} />
                  <div className="border-2 border-dashed border-primary/30 rounded-xl p-8 text-center hover:border-primary/50 transition-colors">
                    {analyzing ? (
                      <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin mb-2" />
                    ) : (
                      <Camera className="w-10 h-10 mx-auto text-primary mb-2" />
                    )}
                    <p className="font-semibold">{analyzing ? "AI analizeaza..." : "Fotografiati carte grise"}</p>
                    <p className="text-xs text-muted-foreground mt-1">Faceti o fotografie clara sau selectati din galerie</p>
                  </div>
                </label>

                {aiData && (
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                    <p className="text-xs font-semibold text-emerald-400 mb-1"><CheckCircle className="w-3 h-3 inline mr-1" /> Date extrase cu AI</p>
                    <p className="text-sm font-bold">{aiData.brand} {aiData.model} ({aiData.year})</p>
                    <p className="text-xs text-muted-foreground">VIN: {aiData.vin} | {aiData.engine_cc}cc | {aiData.fuel_type} | {aiData.engine_type}</p>
                  </div>
                )}

                <Button variant="outline" onClick={() => setStep(2.5)} className="w-full text-xs">
                  Sar peste carte grise — completez manual
                </Button>
              </CardContent>
            </Card>

            {(aiData || step === 2.5) && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <h3 className="font-bold">Date scuter {aiData ? "(verificati/corectati)" : "(manual)"}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Marca</Label><Input value={form.brand} onChange={e => setForm(f => ({...f, brand: e.target.value}))} /></div>
                    <div><Label className="text-xs">Model</Label><Input value={form.model} onChange={e => setForm(f => ({...f, model: e.target.value}))} /></div>
                    <div><Label className="text-xs">An</Label><Input type="number" value={form.year} onChange={e => setForm(f => ({...f, year: e.target.value}))} /></div>
                    <div><Label className="text-xs">Motor (cc)</Label><Input type="number" value={form.engine_cc} onChange={e => setForm(f => ({...f, engine_cc: e.target.value}))} /></div>
                    <div><Label className="text-xs">Culoare</Label><Input value={form.color} onChange={e => setForm(f => ({...f, color: e.target.value}))} /></div>
                    <div><Label className="text-xs">Km</Label><Input type="number" value={form.mileage} onChange={e => setForm(f => ({...f, mileage: e.target.value}))} /></div>
                    <div className="col-span-2"><Label className="text-xs">VIN/Chassis</Label><Input value={form.vin} onChange={e => setForm(f => ({...f, vin: e.target.value}))} /></div>
                  </div>
                  <Button onClick={handleSaveData} disabled={loading} className="w-full">
                    {loading ? "Se salveaza..." : "Salveaza si continua"}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2.5: Manual entry (skip carte grise) */}
        {step === 2.5 && scooterData && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <Badge className="bg-primary text-black mb-2">Dossier nr. {scooterData.dossier_number}</Badge>
              <h3 className="font-bold">Date scuter (manual)</h3>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Marca</Label><Input value={form.brand} onChange={e => setForm(f => ({...f, brand: e.target.value}))} /></div>
                <div><Label className="text-xs">Model</Label><Input value={form.model} onChange={e => setForm(f => ({...f, model: e.target.value}))} /></div>
                <div><Label className="text-xs">An</Label><Input type="number" value={form.year} onChange={e => setForm(f => ({...f, year: e.target.value}))} /></div>
                <div><Label className="text-xs">Motor (cc)</Label><Input type="number" value={form.engine_cc} onChange={e => setForm(f => ({...f, engine_cc: e.target.value}))} /></div>
                <div><Label className="text-xs">Culoare</Label><Input value={form.color} onChange={e => setForm(f => ({...f, color: e.target.value}))} /></div>
                <div><Label className="text-xs">Km</Label><Input type="number" value={form.mileage} onChange={e => setForm(f => ({...f, mileage: e.target.value}))} /></div>
                <div className="col-span-2"><Label className="text-xs">VIN/Chassis</Label><Input value={form.vin} onChange={e => setForm(f => ({...f, vin: e.target.value}))} /></div>
              </div>
              <Button onClick={handleSaveData} disabled={loading} className="w-full">
                {loading ? "Se salveaza..." : "Salveaza si continua"}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Photos */}
        {step === 3 && scooterData && (
          <Card>
            <CardContent className="p-4 space-y-3">
              <Badge className="bg-primary text-black mb-2">Dossier nr. {scooterData.dossier_number}</Badge>
              <h3 className="font-bold flex items-center gap-2"><Camera className="w-4 h-4 text-primary" /> Adaugati fotografii scuter</h3>
              
              <label className="block cursor-pointer">
                <input type="file" multiple accept="image/*" capture="camera" className="hidden" onChange={e => handlePhotos(Array.from(e.target.files))} />
                <div className="border-2 border-dashed border-primary/30 rounded-xl p-8 text-center hover:border-primary/50">
                  {loading ? <Loader2 className="w-10 h-10 mx-auto animate-spin mb-2" /> : <Image className="w-10 h-10 mx-auto text-primary mb-2" />}
                  <p className="font-semibold">{loading ? "Se incarca..." : "Adaugati fotografii"}</p>
                  <p className="text-xs text-muted-foreground">Faceti fotografii sau selectati din galerie</p>
                </div>
              </label>

              <div className="flex gap-2">
                <Button onClick={() => setStep(4)} className="flex-1">Finalizeaza</Button>
                <Button variant="outline" onClick={() => navigate("/scan")} className="flex-1">Scaneaza QR</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Done */}
        {step === 4 && scooterData && (
          <Card className="border-emerald-500/30">
            <CardContent className="p-6 text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
              <h2 className="text-xl font-bold">Scuter inregistrat cu succes!</h2>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Dossier: <strong>{scooterData.dossier_number}</strong></p>
                <p>ID: <strong>{scooterData.scooter_id}</strong></p>
                <p>Parking: <strong>P1 - Receptionat</strong></p>
                {form.brand && <p>{form.brand} {form.model} {form.year}</p>}
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { setStep(1); setScooterData(null); setAiData(null); setForm({ brand: "", model: "", year: "", engine_cc: "", color: "", mileage: "", vin: "" }); }} className="flex-1">
                  <Plus className="w-4 h-4 mr-1" /> Alt scuter
                </Button>
                <Button variant="outline" onClick={() => navigate("/scan")} className="flex-1">Scanner QR</Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
